import { Button, Card, CardContent, CardMedia, Typography } from '@mui/material';
import Image from 'next/image';
import { useOrganisationPersonalWebshop } from 'hooks/useOrganisationPersonalWebshop';
import { useServerTranslation } from 'hooks/useServerTranslation';
import { MarketCodesEnum, MarketType } from 'constants/markets';
import { OrganisationPublicContent } from '../../../generated/graphql';
import { useProfilePhoto } from '../../../hooks/useProfilePhoto';
import useMarket from '../../../stores/useMarket';

type ConsultantResultCardProps = { item: Partial<OrganisationPublicContent>; distanceKm?: number; onFocusOnMap?: () => void };

export function ConsultantResultCard({ item, distanceKm, onFocusOnMap }: ConsultantResultCardProps) {
    const { t } = useServerTranslation();
    const { market } = useMarket();
    const { profilePhotoSrc, fallbackSrc } = useProfilePhoto({ profilePhoto: item.profile_photo_path || undefined });

    const formattedPostalCode = formatPostalCode(item.shipping_postal_code ?? '', market);
    const profilePath = new URL(item.profile_url ?? '').pathname.replace(/\/sv/g, '');

    const { baseUrl } = useOrganisationPersonalWebshop();

    const href = `${baseUrl}${profilePath}`;

    return (
        <Card key={item.id} sx={{ display: 'flex', marginBottom: '16px' }}>
            <CardMedia sx={{ '*': { display: 'block' } }}>
                <Image src={profilePhotoSrc || fallbackSrc} alt="" width={100} height={100} />
            </CardMedia>
            <CardContent sx={{ padding: 1, '&:last-child': { padding: 1 }, position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <Typography sx={{ flex: 1 }}>{`${item.first_name} ${item.last_name}`}</Typography>
                    {distanceKm !== undefined && distanceKm > 0 && (
                        <Typography
                            color="text.secondary"
                            sx={{ fontSize: '0.875rem', marginLeft: '16px', minWidth: '60px', textAlign: 'right' }}
                        >
                            {distanceKm.toFixed(1)}
                            {' '}
                            km
                        </Typography>
                    )}
                </div>
                <Typography>{`${formattedPostalCode}, ${item.shipping_city}`}</Typography>
                <Button
                    component="a"
                    href={href}
                    sx={{ textDecoration: 'none' }}
                >
                    {t('find-consultant.view-profile')}
                </Button>
                {!!onFocusOnMap && (
                    <Button onClick={onFocusOnMap} sx={{ ml: 1 }}>
                        {t('find-consultant.view-on-map')}
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}

function formatPostalCode(postalCode: string, market: MarketType): string {
    if (market === MarketCodesEnum.Sweden) {
        const firstThree = postalCode.slice(0, 3);
        const lastTwo = postalCode.slice(3);

        return `${firstThree} ${lastTwo}`;
    }

    return postalCode;
}
